import {Dispatch, SetStateAction, useEffect, useMemo, useRef, useState} from "react";

interface LastSingleSelect {
    index: number;
    checked: boolean;
}

export type MultiSelectReturnType<TItem> = {
    selected: TItem[];
    setSelected: Dispatch<SetStateAction<TItem[]>>;
    isSelected: (item: TItem) => boolean;
    handleSelect: (checked: boolean, item: TItem, index: number, shiftHeld: boolean) => void;
    selectAll: (checked: boolean) => void;
    allSelected: boolean;
    someSelected: boolean;
}

export function itemId(item: {id: string}): string {
    return item.id;
}

/**
 * Checkbox multi-selection over the currently visible `items`.
 * Shift-click applies the checked state of the last non-shift click to every item between the two.
 * The shift-click anchor resets whenever `items` changes (search, filter, sort).
 * `getKey` must be stable (e.g. a module-level function) since it is a memo dependency.
 */
export function useMultiSelect<TItem>(items: TItem[], getKey: (item: TItem) => string): MultiSelectReturnType<TItem> {
    const [selected, setSelected] = useState<TItem[]>([]);
    const lastSingleSelect = useRef<LastSingleSelect | null>(null);

    useEffect(() => {
        lastSingleSelect.current = null;
    }, [items]);

    const {allSelected, someSelected} = useMemo(() => {
        const allSelected = items.length > 0 && items.every(p => selected.some(x => getKey(x) === getKey(p)));
        const someSelected = !allSelected && selected.some(p => items.some(x => getKey(x) === getKey(p)));
        return {allSelected, someSelected};
    }, [selected, items, getKey]);

    function isSelected(item: TItem): boolean {
        return selected.some(x => getKey(x) === getKey(item));
    }

    function handleSelect(checked: boolean, item: TItem, index: number, shiftHeld: boolean): void {
        if(shiftHeld && lastSingleSelect.current && lastSingleSelect.current.index !== index) {
            const lastSelected = lastSingleSelect.current;
            let startIndex = index;
            let endIndex = lastSelected.index;
            if(startIndex > endIndex) {
                const tmp = startIndex;
                startIndex = endIndex;
                endIndex = tmp;
            }
            const affectedItems = items.slice(startIndex, endIndex + 1);
            setSelected(prev => lastSelected.checked
                ? [...prev, ...affectedItems.filter(p => !prev.some(x => getKey(x) === getKey(p)))]
                : prev.filter(x => !affectedItems.some(y => getKey(y) === getKey(x))));
        } else {
            setSelected(prev => checked
                ? [...prev, item]
                : prev.filter(x => getKey(x) !== getKey(item)));
            lastSingleSelect.current = {index, checked};
        }
    }

    function selectAll(checked: boolean): void {
        setSelected(prev => checked
            ? [...prev, ...items.filter(p => !prev.some(x => getKey(x) === getKey(p)))]
            : prev.filter(p => !items.some(x => getKey(x) === getKey(p))));
    }

    return {selected, setSelected, isSelected, handleSelect, selectAll, allSelected, someSelected};
}
